import {
  EntityLinks,
  Goal,
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  LifeArea,
  RelationshipUpdate,
  UpdateGoal,
} from '@griply/types';
import { useCallback } from 'react';

export type GoalLinkChanges = {
  goalParentChanges?: GoalParentLinkChange[];
  goalLifeAreaChanges?: GoalLifeAreaLinkChange[];
};

export type MergeGoalLinkChanges = (options: {
  updatedGoals: UpdateGoal[];
  goalParentChanges?: GoalParentLinkChange[];
  goalLifeAreaChanges?: GoalLifeAreaLinkChange[];
  goals: Goal[];
  links: EntityLinks;
  lifeAreas?: LifeArea[];
}) => { goalUpdates: UpdateGoal[]; relationshipUpdates: RelationshipUpdate[] };

export type UseMergeGoalLinkChangesOptions = {
  goals: Goal[];
  links: EntityLinks;
  // A migrating goal freezes the colour it inherits, which may come from a life area.
  lifeAreas?: LifeArea[];
  // Injected: mergeGoalLinkChanges lives in @griply/services, which this package may not import.
  mergeGoalLinkChanges: MergeGoalLinkChanges;
  updateRelationships: (relationshipUpdates: RelationshipUpdate[]) => void;
};

// One dispatch per drop: both link kinds resolve into a single relationship write per goal.
export const useMergeGoalLinkChanges = ({
  goals,
  links,
  lifeAreas,
  mergeGoalLinkChanges,
  updateRelationships,
}: UseMergeGoalLinkChangesOptions) =>
  useCallback(
    (
      updatedGoals: UpdateGoal[],
      { goalParentChanges, goalLifeAreaChanges }: GoalLinkChanges = {},
    ): UpdateGoal[] => {
      const { goalUpdates, relationshipUpdates } = mergeGoalLinkChanges({
        updatedGoals,
        goalParentChanges,
        goalLifeAreaChanges,
        goals,
        links,
        lifeAreas,
      });

      if (relationshipUpdates.length) {
        updateRelationships(relationshipUpdates);
      }

      return goalUpdates;
    },
    [goals, lifeAreas, links, mergeGoalLinkChanges, updateRelationships],
  );
